"use client"

import { useEffect, useState } from "react"
import { HorizontalCarousel } from "@/components/brand-kit/horizontal-carousel"
import { ProductCarouselCard, type CarouselProduct } from "@/components/brand-kit/product-carousel-card"
import { getBestsellers, type RecommendedProduct } from "@/lib/recommendations"
import { badgesFromRecommended } from "@/components/badges/from-product"

const formatUSD = (value?: number) =>
  typeof value === "number" && value > 0
    ? `$${value.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : ""

function toCarouselProduct(p: RecommendedProduct): CarouselProduct {
  const hasDiscount =
    typeof p.originalPrice === "number" &&
    typeof p.price === "number" &&
    p.originalPrice > p.price
  return {
    brand: p.brand,
    title: p.title,
    price: formatUSD(p.price),
    originalPrice: hasDiscount ? formatUSD(p.originalPrice) : undefined,
    badge: hasDiscount ? "Price drop" : "Bestseller",
    badgeColor: hasDiscount ? "bg-red-500" : "bg-[#ff9951]",
    image: p.images?.[0] || "/placeholder.svg",
    images: p.images ?? [],
    href: p.canonical_url || `/products/${p.id}`,
    rating: p.rating,
    reviews: p.reviews,
    // Canonical badge set so a product's badges match every other surface.
    badges: badgesFromRecommended(p),
  }
}

interface BestsellersCarouselProps {
  eyebrow?: string
  title?: string
  description?: string
  limit?: number
}

/**
 * BestsellersCarousel — drop-in bestsellers rail for the /rebates/* pages.
 *
 * Fetches client-side via `getBestsellers(limit)` and renders via the
 * brand-kit `HorizontalCarousel` + `ProductCarouselCard` pair (the same
 * design element used by `/tools` and `/brands`). Renders nothing while
 * the fetch is pending or empty so it never shows a skeleton or breaks
 * the page when Supabase is unreachable.
 *
 * Replaces the legacy `RebateBestsellers` / `LocalBestsellers` widgets
 * that rendered the bespoke `BestsellersBlock` carousel.
 */
export function BestsellersCarousel({
  eyebrow = "Trending this week",
  title = "Bestsellers on Fridge.com",
  description = "Best-rated refrigerators and freezers across every style, updated regularly.",
  limit = 8,
}: BestsellersCarouselProps) {
  const [products, setProducts] = useState<RecommendedProduct[]>([])

  useEffect(() => {
    let cancelled = false
    async function fetchBestsellers() {
      try {
        const data = await getBestsellers(limit)
        if (!cancelled) setProducts(data)
      } catch {
        // Silently swallow — bestsellers are optional, never block the page.
      }
    }
    fetchBestsellers()
    return () => {
      cancelled = true
    }
  }, [limit])

  if (products.length === 0) return null

  const cards = products.map(toCarouselProduct)

  return (
    <section className="my-8">
      <div className="max-w-[1320px] mx-auto px-6 md:px-8">
        <HorizontalCarousel
          eyebrow={eyebrow}
          title={title}
          description={description}
          bgClass="bg-gray-50"
          className="!mt-0"
        >
          {cards.map((p, i) => (
            <ProductCarouselCard key={`${p.brand}-${p.title}-${i}`} product={p} />
          ))}
        </HorizontalCarousel>
      </div>
    </section>
  )
}
